import { useEffect, useRef, useState } from 'react';
import '../Dashboard.scss';
import * as echarts from 'echarts';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {
  COMPACT_FONT_SIZE,
  COMPACT_LABEL_SIZE,
  downloadCSV,
  downloadIcon,
  FULL_FONT_SIZE,
  FULL_LABEL_SIZE,
} from './utils';

const Chart = ({
  emissions,
  options,
  compact,
}: {
  emissions: any;
  options: any;
  compact?: boolean;
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = echarts.init(chartRef.current);

    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  const updateChartOptions = () => {
    if (!chartInstance.current || !emissions || emissions.length === 0) return;

    const option = {
      title: {
        text: options.title,
        left: 'left',

        textStyle: {
          fontSize: !isFullscreen
            ? COMPACT_FONT_SIZE.toString()
            : FULL_FONT_SIZE.toString() + 'px',
        },
      },
      grid: {
        top: compact ? 50 : 60,
        bottom: 110,
        left: compact ? 70 : 50,
        right: compact ? 20 : 30,
      },
      dataset: emissions,
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any) {
          const data = params[0].data;
          const yValue1 = Number(data['Total Emission']).toFixed(1);
          const yValue2 = Number(data['Population EF']).toFixed(1);

          return `Operator: ${yValue2}<br>Population: ${yValue1}`;
        },
      },
      xAxis: {
        type: 'category',
        axisLabel: {
          interval: 0,
          rotate: 90,
          overflow: 'break',
          width: 100,
          fontSize: !isFullscreen ? COMPACT_LABEL_SIZE : FULL_LABEL_SIZE,
          formatter: function (value: string) {
            return value;
          },
        },
      },
      yAxis: {
        type: 'value',
        name: options.yLabel,
        nameRotate: 90,
        position: 'left',
        offset: 0,
        nameLocation: 'middle',
        nameGap: 50,
      },
      series: [
        {
          type: 'bar',
          itemStyle: {
            color: 'rgba(84, 112, 198, 0.6)',
          },
        },
        {
          type: 'bar',
          itemStyle: {
            color: 'rgba(84,198,86,0.6)',
          },
        },
      ],
      toolbox: {
        show: isFullscreen,
        right: '3%',
        feature: {
          saveAsImage: {},
          // myDownload: {
          //   show: true,
          //   title: 'Download data',
          //   icon: downloadIcon,
          //   onclick: function () {
          //     downloadCSV([insertedNames, insertedData] || []);
          //   },
          // },
          dataZoom: [
            {
              type: 'slider',
            },
            {
              type: 'inside',
            },
          ],
        },
      },
      animation: false,
    };

    chartInstance.current.setOption(option);
  };

  useEffect(() => {
    updateChartOptions();

    if (chartInstance.current) {
      chartInstance.current.resize();
    }
  }, [
    emissions,
    isFullscreen,
    options.title,
    options.type,
    options.xLabel,
    compact,
  ]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`chart-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <IconButton
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '10px',
          right: isFullscreen ? '24px' : '10px',
          zIndex: 1000,
        }}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
      <div ref={chartRef} className={'chart pie'} />
    </div>
  );
};

export default Chart;
